import { supabase } from '../lib/supabase';

/**
 * Recording Service
 *
 * Fetches a short-lived, signed Vapi recording URL via the `get-recording`
 * edge function. As of July 15, 2026 the raw `recording_url` values stored in
 * call_reports are no longer publicly fetchable — recordings must be retrieved
 * through an authenticated Vapi API call, which the edge function performs
 * server-side using the private key.
 *
 * The returned URL is short-lived, so callers should request a fresh one on
 * each playback rather than caching it.
 */

// Map an edge-function error to a user-friendly message based on HTTP status.
const messageForError = async (error) => {
  const status = error?.context?.status;
  switch (status) {
    case 401:
      return 'Your session expired. Please sign in again.';
    case 403:
      return 'You are not authorized to access recordings.';
    case 404:
      return 'Recording not available for this call.';
    default:
      return 'Could not load the recording. Please try again.';
  }
};

// Get a playable, signed recording URL for a call.
// type: 'mono' (default) or 'stereo'.
export const getRecordingUrl = async (callId, type = 'mono') => {
  if (!callId) {
    throw new Error('A call ID is required to load a recording.');
  }

  const { data, error } = await supabase.functions.invoke('get-recording', {
    body: { call_id: callId, type },
  });

  if (error) {
    throw new Error(await messageForError(error));
  }
  if (!data?.url) {
    throw new Error('Could not load the recording. Please try again.');
  }

  return data.url;
};
